import { z } from 'zod';

/**
 * Creates a safe wrapper around a Storage object that catches and handles errors gracefully.
 * This prevents storage-related errors from crashing the application in environments where
 * storage may be disabled, full, or otherwise unavailable.
 *
 * @param storage - The Storage object to wrap (e.g., localStorage, sessionStorage)
 * @returns A Storage-compatible object that safely handles all storage operations
 *
 * @example
 * ```typescript
 * const safeLocalStorage = createSafeStorage(window.localStorage);
 * safeLocalStorage.setItem('key', 'value'); // Won't throw if storage is disabled
 * const value = safeLocalStorage.getItem('key'); // Returns null on error
 * ```
 */
function createSafeStorage(storage: Storage): Storage {
  const handleError = (err: unknown) => {
    // eslint-disable-next-line no-console
    console.warn('Error accessing storage:', err);
  };

  return {
    get length(): number {
      try {
        return storage.length;
      } catch (err) {
        handleError(err);
        return 0;
      }
    },
    clear(): void {
      try {
        storage.clear();
      } catch (err) {
        handleError(err);
      }
    },
    getItem(key: string): string | null {
      try {
        return storage.getItem(key);
      } catch (err) {
        handleError(err);
        return null;
      }
    },
    key(index: number): string | null {
      try {
        return storage.key(index);
      } catch (err) {
        handleError(err);
        return null;
      }
    },
    removeItem(key: string): void {
      try {
        storage.removeItem(key);
      } catch (err) {
        handleError(err);
      }
    },
    setItem(key: string, value: string): void {
      try {
        storage.setItem(key, value);
      } catch (err) {
        handleError(err);
      }
    },
  };
}

/**
 * Pre-configured safe localStorage instance that wraps the native localStorage
 * with error handling to prevent storage-related errors from crashing the application.
 *
 * @example
 * ```typescript
 * import { safeLocalStorage } from '@repo/lib/storage';
 *
 * safeLocalStorage.setItem('key', 'value'); // Won't throw if storage is disabled
 * const value = safeLocalStorage.getItem('key'); // Returns null on error
 * ```
 */
export const safeLocalStorage = createSafeStorage(window.localStorage);

/**
 * Pre-configured safe sessionStorage instance that wraps the native sessionStorage
 * with error handling to prevent storage-related errors from crashing the application.
 *
 * @example
 * ```typescript
 * import { safeSessionStorage } from '@repo/lib/storage';
 *
 * safeSessionStorage.setItem('temp', 'data'); // Won't throw if storage is disabled
 * const value = safeSessionStorage.getItem('temp'); // Returns null on error
 * ```
 */
export const safeSessionStorage = createSafeStorage(window.sessionStorage);

/**
 * Retrieves and parses a JSON value from storage with Zod schema validation.
 * Provides type safety by validating the parsed data against the provided schema.
 *
 * @template T - The Zod schema type
 * @param storage - Storage instance to retrieve from (e.g., localStorage, sessionStorage)
 * @param key - Storage key to retrieve the value from
 * @param schema - Zod schema to validate and parse the stored data
 * @returns The parsed and validated data of type T, or null if key doesn't exist or validation fails
 *
 * @example
 * ```typescript
 * const userSchema = z.object({ name: z.string(), age: z.number() });
 * const user = getStorageJson(localStorage, 'user', userSchema);
 * // user is typed as { name: string; age: number } | null
 * ```
 */
export function getStorageJson<T extends z.ZodTypeAny>(
  storage: Storage,
  key: string,
  schema: T,
): z.infer<T> | null {
  const value = storage.getItem(key);
  if (value === null) return null;
  const json = JSON.parse(value);
  return schema.parse(json);
}

/**
 * Serializes and stores a value as JSON in storage.
 *
 * @param storage - Storage instance to store in (e.g., localStorage, sessionStorage)
 * @param key - Storage key to store the value under
 * @param value - Value to serialize and store
 *
 * @example
 * ```typescript
 * const user = { name: 'John', age: 30 };
 * setStorageJson(localStorage, 'user', user);
 * ```
 */
export function setStorageJson(storage: Storage, key: string, value: unknown) {
  const str = JSON.stringify(value);
  storage.setItem(key, str);
}
